/**
 * SettingsHome - Card-based settings home page
 *
 * Displays 5 main settings sections as cards:
 * - Your community: Name, branding, URL
 * - Member experience: Onboarding, fields, permissions
 * - Integrations: Connected services
 * - Team: Team members and roles
 * - Billing: Subscription and payment
 *
 * Design Philosophy:
 * - See everything at a glance
 * - No sidebar, just cards
 * - Each card describes what you'll find
 */

import React from 'react';
import { Link } from 'react-router-dom';
import {
  Building2,
  Users,
  Plug,
  UserCog,
  CreditCard,
  ChevronRight,
  ExternalLink,
} from 'lucide-react';
import { useAtomValue } from 'jotai';
import { currentCommunityAtom } from '@/lib/stores/community';
import { cn } from '@/lib/utils';

interface SettingsCard {
  id: string;
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  href: string;
  badge?: string;
}

const SETTINGS_CARDS: SettingsCard[] = [
  {
    id: 'community',
    title: 'Your community',
    description: 'Name, branding, URL, and welcome message',
    icon: Building2,
    href: '/settings/settings-v2/community',
  },
  {
    id: 'members',
    title: 'Member experience',
    description: 'Onboarding questions, profile fields, and permissions',
    icon: Users,
    href: '/settings/settings-v2/members',
  },
  {
    id: 'integrations',
    title: 'Integrations',
    description: 'LinkedIn, Slack, calendar, and other connected services',
    icon: Plug,
    href: '/settings/settings-v2/integrations',
  },
  {
    id: 'team',
    title: 'Team',
    description: 'People who help run your community',
    icon: UserCog,
    href: '/settings/settings-v2/team',
  },
  {
    id: 'billing',
    title: 'Billing',
    description: 'Subscription, payment method, and invoices',
    icon: CreditCard,
    href: '/settings/settings-v2/billing',
  },
];

export function SettingsHome() {
  const community = useAtomValue(currentCommunityAtom);

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      {/* Header */}
      <header className="mb-10">
        <h1 className="serif-display text-[28px] text-[#1A1A19]">Settings</h1>
        <p className="text-[15px] text-[#6B6966] mt-1">
          Configure {community?.name || 'your community'}
        </p>
      </header>

      {/* Settings Cards */}
      <div className="space-y-4">
        {SETTINGS_CARDS.map((card) => {
          const Icon = card.icon;
          return (
            <Link
              key={card.id}
              to={card.href}
              className={cn(
                'flex items-center p-5 bg-white border border-[#E8E6E3] rounded-xl',
                'hover:border-[#CC785C]/30 hover:bg-[#CC785C]/[0.02]',
                'transition-all duration-150 group'
              )}
            >
              <div className="w-12 h-12 rounded-lg bg-[#F5F3F0] flex items-center justify-center">
                <Icon className="w-6 h-6 text-[#6B6966] group-hover:text-[#CC785C] transition-colors" />
              </div>
              <div className="flex-1 ml-4">
                <div className="flex items-center space-x-2">
                  <h2 className="text-[17px] font-medium text-[#1A1A19]">
                    {card.title}
                  </h2>
                  {card.badge && (
                    <span className="px-2 py-0.5 bg-[#CC785C]/10 text-[#CC785C] text-[11px] font-medium rounded-full">
                      {card.badge}
                    </span>
                  )}
                </div>
                <p className="text-[14px] text-[#6B6966] mt-0.5">
                  {card.description}
                </p>
              </div>
              <ChevronRight className="w-5 h-5 text-[#6B6966] group-hover:text-[#CC785C] transition-colors" />
            </Link>
          );
        })}
      </div>

      {/* Advanced Settings Link */}
      <div className="mt-10 pt-8 border-t border-[#E8E6E3]">
        <p className="text-[13px] text-[#6B6966] mb-3">
          Looking for advanced settings?
        </p>
        <Link
          to="/legacy-settings"
          className="inline-flex items-center text-[14px] text-[#CC785C] hover:underline"
        >
          Open full settings
          <ExternalLink className="w-3.5 h-3.5 ml-1" />
        </Link>
      </div>
    </div>
  );
}
